import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Layers, 
  Plus, 
  TrendingUp,
  Percent,
  DollarSign,
  RefreshCw,
} from 'lucide-react';
import { Card, Button, Input, Badge, Modal } from '../components/ui';
import { api } from '../lib/api';
import { ENDPOINTS } from '../config/api';
import { cn } from '../lib/utils';

interface Pool {
  id: string;
  name: string;
  provider: string;
  network: string;
  tokenSymbol: string;
  apy: number;
  tvl: string;
  minDeposit: string;
}

interface PoolPosition {
  id: string;
  virtualAccountId: string;
  poolId: string;
  provider: string;
  network: string;
  tokenSymbol: string;
  depositedAmount: string;
  currentValue: string;
  rewards: string;
  apyAtDeposit: number;
  status: string;
  createdAt: string;
}

const NETWORKS = [
  { value: '', label: 'Todas as redes' },
  { value: 'ETHEREUM', label: 'Ethereum' },
  { value: 'BSC', label: 'BNB Smart Chain' },
  { value: 'SOLANA', label: 'Solana' },
];

export function DefiPools() {
  const queryClient = useQueryClient();
  const [selectedVirtualAccount, setSelectedVirtualAccount] = useState('');
  const [selectedNetwork, setSelectedNetwork] = useState('');

  // Modals
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [selectedPool, setSelectedPool] = useState<Pool | null>(null);
  const [selectedPosition, setSelectedPosition] = useState<PoolPosition | null>(null);

  // Fetch virtual accounts
  const { data: virtualAccountsData } = useQuery({
    queryKey: ['virtual-accounts'],
    queryFn: () => api.get(ENDPOINTS.virtualAccounts.list).then(res => res.data),
  });

  // Extract accounts array from response
  const virtualAccounts = virtualAccountsData?.accounts || virtualAccountsData?.data || (Array.isArray(virtualAccountsData) ? virtualAccountsData : []);

  // Fetch available pools
  const { data: poolsData, isLoading: poolsLoading, refetch: refetchPools } = useQuery({
    queryKey: ['defi-pools', selectedNetwork],
    queryFn: () => api.get(ENDPOINTS.defiPools.list, { 
      params: selectedNetwork ? { network: selectedNetwork } : {} 
    }).then(res => res.data),
  });

  // Extract pools array from response
  const pools = poolsData?.pools || poolsData || [];

  // Fetch user positions
  const { data: positionsData, isLoading: positionsLoading } = useQuery({
    queryKey: ['pool-positions', selectedVirtualAccount],
    queryFn: () => api.get(ENDPOINTS.defiPools.positions, {
      params: { virtualAccountId: selectedVirtualAccount }
    }).then(res => res.data),
    enabled: !!selectedVirtualAccount,
  });

  // Extract positions array from response
  const positions = positionsData?.positions || positionsData || [];

  const getStatusBadge = (status: string) => {
    const variants: Record<string, 'success' | 'warning' | 'error'> = {
      ACTIVE: 'success',
      WITHDRAWN: 'warning',
      PENDING: 'warning',
    };
    return <Badge variant={variants[status] || 'warning'}>{status}</Badge>;
  };

  const calculatePnL = (position: PoolPosition) => {
    const deposited = parseFloat(position.depositedAmount);
    const current = parseFloat(position.currentValue);
    const rewards = parseFloat(position.rewards);
    const total = current + rewards;
    const pnl = total - deposited;
    const pnlPercent = deposited > 0 ? (pnl / deposited) * 100 : 0;
    return { pnl, pnlPercent };
  };

  const openDepositModal = (pool: Pool) => {
    setSelectedPool(pool);
    setShowDepositModal(true);
  };

  const openWithdrawModal = (position: PoolPosition) => {
    setSelectedPosition(position);
    setShowWithdrawModal(true);
  };

  // Build account options for select
  const accountOptions = [
    { value: '', label: 'Selecione uma conta' },
    ...(virtualAccounts?.map((account: any) => ({
      value: account.id,
      label: account.user?.name || account.id.slice(0, 8),
    })) || []),
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Pools DeFi</h1>
          <p className="text-surface-400 mt-1">Invista em pools de liquidez e ganhe rendimentos</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={selectedNetwork}
            onChange={(e) => setSelectedNetwork(e.target.value)}
            className="input w-48"
          >
            {NETWORKS.map(net => (
              <option key={net.value} value={net.value}>{net.label}</option>
            ))}
          </select>
          <select
            value={selectedVirtualAccount}
            onChange={(e) => setSelectedVirtualAccount(e.target.value)}
            className="input w-64"
          >
            {accountOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Stats Cards */}
      {selectedVirtualAccount && positions?.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <p className="text-sm text-surface-400">Total Investido</p>
                <p className="text-xl font-bold text-white">
                  {positions?.reduce((acc: number, p: PoolPosition) => 
                    acc + parseFloat(p.depositedAmount), 0
                  ).toFixed(2)} USDT
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-surface-400">Valor Atual</p>
                <p className="text-xl font-bold text-green-400">
                  {positions?.reduce((acc: number, p: PoolPosition) => 
                    acc + parseFloat(p.currentValue), 0
                  ).toFixed(2)} USDT
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                <Percent className="w-5 h-5 text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-surface-400">Rewards</p>
                <p className="text-xl font-bold text-purple-400">
                  {positions?.reduce((acc: number, p: PoolPosition) => 
                    acc + parseFloat(p.rewards), 0
                  ).toFixed(4)}
                </p>
              </div>
            </div>
          </Card>
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-yellow-500/20 flex items-center justify-center">
                <Layers className="w-5 h-5 text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-surface-400">Posições Ativas</p>
                <p className="text-xl font-bold text-white">
                  {positions?.filter((p: PoolPosition) => p.status === 'ACTIVE').length || 0}
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}


      {/* Available Pools */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Pools Disponíveis</h3>
          <Button variant="ghost" size="sm" onClick={() => refetchPools()}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Atualizar
          </Button>
        </div>

        {poolsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-48 bg-surface-800 rounded-lg animate-pulse" />
            ))}
          </div>
        ) : pools?.length === 0 ? (
          <div className="text-center py-12 text-surface-400">
            <Layers className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Nenhum pool disponível no momento</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {pools?.map((pool: Pool) => (
              <div
                key={pool.id}
                className="p-4 rounded-lg border border-surface-700 bg-surface-800/50 hover:border-primary-500/50 transition-colors"
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-lg font-bold text-white">{pool.name}</span>
                  <Badge variant="success">{pool.network}</Badge>
                </div>
                
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-surface-400">Provider:</span>
                    <span className="text-white">{pool.provider}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-surface-400">Token:</span>
                    <span className="text-white">{pool.tokenSymbol}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-surface-400">APY:</span>
                    <span className="text-green-400 font-bold">{pool.apy.toFixed(2)}%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-surface-400">TVL:</span>
                    <span className="text-white">${pool.tvl}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-surface-400">Mín. Depósito:</span>
                    <span className="text-white">{pool.minDeposit} {pool.tokenSymbol}</span>
                  </div>
                </div>

                <Button
                  className="w-full"
                  onClick={() => openDepositModal(pool)}
                  disabled={!selectedVirtualAccount}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Depositar
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* User Positions */}
      {selectedVirtualAccount && (
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Minhas Posições</h3>

          {positionsLoading ? (
            <div className="space-y-3">
              {[1, 2].map(i => (
                <div key={i} className="h-24 bg-surface-800 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : positions?.length === 0 ? (
            <div className="text-center py-8 text-surface-400">
              <Layers className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Você ainda não tem posições em pools</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-surface-400 text-sm border-b border-surface-700">
                    <th className="pb-3">Pool</th>
                    <th className="pb-3">Rede</th>
                    <th className="pb-3 text-right">Depositado</th>
                    <th className="pb-3 text-right">Valor Atual</th>
                    <th className="pb-3 text-right">Rewards</th>
                    <th className="pb-3 text-right">P&L</th>
                    <th className="pb-3">Status</th>
                    <th className="pb-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {positions?.map((position: PoolPosition) => {
                    const { pnl, pnlPercent } = calculatePnL(position);
                    return (
                      <tr key={position.id} className="border-b border-surface-700/50">
                        <td className="py-3">
                          <div>
                            <p className="text-white font-medium">{position.provider}</p>
                            <p className="text-sm text-surface-400">{position.tokenSymbol}</p>
                          </div>
                        </td>
                        <td className="py-3">
                          <Badge variant="success">{position.network}</Badge>
                        </td>
                        <td className="py-3 text-right text-white">
                          {parseFloat(position.depositedAmount).toFixed(4)}
                        </td>
                        <td className="py-3 text-right text-white">
                          {parseFloat(position.currentValue).toFixed(4)}
                        </td>
                        <td className="py-3 text-right text-purple-400">
                          {parseFloat(position.rewards).toFixed(6)}
                        </td>
                        <td className={cn(
                          'py-3 text-right font-medium',
                          pnl >= 0 ? 'text-green-400' : 'text-red-400'
                        )}>
                          {pnl >= 0 ? '+' : ''}{pnl.toFixed(4)} ({pnlPercent.toFixed(2)}%)
                        </td>
                        <td className="py-3">{getStatusBadge(position.status)}</td>
                        <td className="py-3">
                          {position.status === 'ACTIVE' && (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() => openWithdrawModal(position)}
                            >
                              Resgatar
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      )}

      {/* Deposit Modal */}
      {selectedPool && (
        <DepositModal
          isOpen={showDepositModal}
          onClose={() => {
            setShowDepositModal(false);
            setSelectedPool(null);
          }}
          pool={selectedPool}
          virtualAccountId={selectedVirtualAccount}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['pool-positions'] });
            setShowDepositModal(false);
            setSelectedPool(null);
          }}
        />
      )}

      {/* Withdraw Modal */}
      {selectedPosition && (
        <WithdrawModal
          isOpen={showWithdrawModal}
          onClose={() => {
            setShowWithdrawModal(false);
            setSelectedPosition(null);
          }}
          position={selectedPosition}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['pool-positions'] });
            setShowWithdrawModal(false);
            setSelectedPosition(null);
          }}
        />
      )}
    </div>
  );
}


// Deposit Modal
function DepositModal({
  isOpen,
  onClose,
  pool,
  virtualAccountId,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  pool: Pool;
  virtualAccountId: string;
  onSuccess: () => void;
}) {
  const [amount, setAmount] = useState('');
  const [agentId, setAgentId] = useState('');

  const depositMutation = useMutation({
    mutationFn: (data: { virtualAccountId: string; poolId: string; amountBrl: number; agentId?: string }) =>
      api.post(ENDPOINTS.crypto.poolDeposit(data.poolId), {
        virtualAccountId: data.virtualAccountId,
        amountBrl: data.amountBrl,
        agentId: data.agentId,
      }),
    onSuccess,
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    depositMutation.mutate({
      virtualAccountId,
      poolId: pool.id,
      amountBrl: Math.round(parseFloat(amount) * 100), // Convert to centavos
      agentId: agentId || undefined,
    });
  };

  const estimatedYearlyReturn = parseFloat(amount || '0') * (pool.apy / 100);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Depositar em ${pool.name}`}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="p-4 bg-surface-800/50 rounded-lg space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-surface-400">Pool:</span>
            <span className="text-white">{pool.name}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-surface-400">Rede:</span>
            <span className="text-white">{pool.network}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-surface-400">APY Atual:</span>
            <span className="text-green-400 font-bold">{pool.apy.toFixed(2)}%</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-surface-400">Depósito Mínimo:</span>
            <span className="text-white">{pool.minDeposit} {pool.tokenSymbol}</span>
          </div>
        </div>

        <Input
          label={`Quantidade (${pool.tokenSymbol})`}
          type="number"
          step="0.0001"
          min={pool.minDeposit}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          required
        />

        <Input
          label="ID do Agente (opcional)"
          value={agentId}
          onChange={(e) => setAgentId(e.target.value)}
          placeholder="Para comissão de indicação"
        />

        {amount && (
          <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
            <p className="text-sm text-green-400">
              Rendimento estimado anual: <strong>{estimatedYearlyReturn.toFixed(4)} {pool.tokenSymbol}</strong>
            </p>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={depositMutation.isPending}>
            {depositMutation.isPending ? 'Depositando...' : 'Depositar'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// Withdraw Modal
function WithdrawModal({
  isOpen,
  onClose,
  position,
  onSuccess,
}: {
  isOpen: boolean;
  onClose: () => void;
  position: PoolPosition;
  onSuccess: () => void;
}) {
  const pnl = parseFloat(position.currentValue) + parseFloat(position.rewards) - parseFloat(position.depositedAmount);
  const pnlPercent = (pnl / parseFloat(position.depositedAmount)) * 100;
  const total = parseFloat(position.currentValue) + parseFloat(position.rewards);

  const withdrawMutation = useMutation({
    mutationFn: () => api.post(ENDPOINTS.crypto.poolWithdraw(position.id), { 
      virtualAccountId: position.virtualAccountId 
    }),
    onSuccess,
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Resgatar Posição">
      <div className="space-y-4">
        <div className="p-4 bg-surface-800/50 rounded-lg space-y-3">
          <div className="flex justify-between">
            <span className="text-surface-400">Pool:</span>
            <span className="text-white">{position.provider}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-surface-400">Depositado:</span>
            <span className="text-white">{parseFloat(position.depositedAmount).toFixed(4)} {position.tokenSymbol}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-surface-400">Valor Atual:</span>
            <span className="text-white">{parseFloat(position.currentValue).toFixed(4)} {position.tokenSymbol}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-surface-400">Rewards:</span>
            <span className="text-purple-400">{parseFloat(position.rewards).toFixed(6)}</span>
          </div>
          <div className="flex justify-between pt-2 border-t border-surface-700">
            <span className="text-surface-400">Total a Receber:</span>
            <span className="text-green-400 font-bold">{total.toFixed(4)} {position.tokenSymbol}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-surface-400">P&L:</span>
            <span className={pnl >= 0 ? 'text-green-400' : 'text-red-400'}>
              {pnl >= 0 ? '+' : ''}{pnl.toFixed(4)} ({pnlPercent.toFixed(2)}%)
            </span>
          </div>
        </div>

        <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
          <p className="text-sm text-yellow-400">
            ⚠️ O resgate pode levar alguns minutos para ser processado na blockchain.
          </p>
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button 
            onClick={() => withdrawMutation.mutate()}
            disabled={withdrawMutation.isPending}
          >
            {withdrawMutation.isPending ? 'Resgatando...' : 'Confirmar Resgate'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default DefiPools;
